import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import type { ApiSingleResponseWithData, PaginatedResult } from '../../interfaces/api-response.interface';
import {
  NotificationType,
  type NotifDto,
  type UnreadCountDto,
  type AppNotification,
  type AppNotificationType,
} from '../../interfaces/notification.interface';

const BASE = `${environment.baseUrl}notifications`;

function mapType(type: NotificationType): AppNotificationType {
  const map: Partial<Record<NotificationType, AppNotificationType>> = {
    [NotificationType.Info]:       'info',
    [NotificationType.Success]:    'success',
    [NotificationType.Warning]:    'warning',
    [NotificationType.System]:     'system',
    [NotificationType.Heritage]:   'info',
    [NotificationType.Succession]: 'info',
    [NotificationType.Account]:    'system',
  };
  return map[type] ?? 'info';
}

function mapTypeToTitle(type: NotificationType): string {
  const titles: Partial<Record<NotificationType, string>> = {
    [NotificationType.Info]:       'Information',
    [NotificationType.Success]:    'Succès',
    [NotificationType.Warning]:    'Avertissement',
    [NotificationType.System]:     'Système',
    [NotificationType.Heritage]:   'Patrimoine',
    [NotificationType.Succession]: 'Succession',
    [NotificationType.Account]:    'Compte',
  };
  return titles[type] ?? 'Notification';
}

export function notifDtoToApp(dto: NotifDto): AppNotification {
  return {
    id:        dto.id,
    type:      mapType(dto.type),
    title:     mapTypeToTitle(dto.type),
    message:   dto.texte ?? '',
    createdAt: dto.creeLe ?? '',
    read:      dto.lu,
    link:      dto.action ?? undefined,
  };
}

export interface NotificationsParams {
  page?:     number;
  pageSize?: number;
  type?:     number;
  read?:     boolean;
  q?:        string;
}

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private http = inject(HttpClient);

  getAll(params?: NotificationsParams): Observable<ApiSingleResponseWithData<PaginatedResult<NotifDto>>> {
    let httpParams = new HttpParams();
    if (params?.page     !== undefined) httpParams = httpParams.set('page',     params.page);
    if (params?.pageSize !== undefined) httpParams = httpParams.set('pageSize', params.pageSize);
    if (params?.type     !== undefined) httpParams = httpParams.set('type',     params.type);
    if (params?.read     !== undefined) httpParams = httpParams.set('read',     params.read);
    if (params?.q)                      httpParams = httpParams.set('q',        params.q);
    return this.http.get<ApiSingleResponseWithData<PaginatedResult<NotifDto>>>(BASE, { params: httpParams });
  }

  getUnreadCount(): Observable<ApiSingleResponseWithData<UnreadCountDto>> {
    return this.http.get<ApiSingleResponseWithData<UnreadCountDto>>(`${BASE}/unread-count`);
  }

  markAsRead(id: number): Observable<ApiSingleResponseWithData<NotifDto>> {
    return this.http.put<ApiSingleResponseWithData<NotifDto>>(`${BASE}/${id}/read`, {});
  }

  markAllAsRead(): Observable<ApiSingleResponseWithData<UnreadCountDto>> {
    return this.http.put<ApiSingleResponseWithData<UnreadCountDto>>(`${BASE}/read-all`, {});
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${BASE}/${id}`);
  }
}
